import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import { api, formatDate, mensajeError } from '../lib/api';
import {
  useTelefonoClinica, respuestaPerdida, cuandoReintentar, aQuienLlamar, conMayuscula,
} from '../lib/enlacePaciente';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Check, Camera, Image as ImageIcon, FileText, Loader2, Download, Trash2, Scale,
  ClipboardList, HeartPulse, Stethoscope, Lock, MapPin, PlayCircle, FilePen, Landmark,
  Copy, AlertTriangle, MessageCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Un paso. Se hacen POR ORDEN (2026-08-03): hasta que no cierras el anterior,
 * el siguiente se ve pero está bloqueado. Se enseña igualmente, en gris, para
 * que el paciente sepa lo que le queda por delante.
 */
function Paso({ n, titulo, hecho, bloqueado, icono: Icono, id, children }) {
  return (
    <section
      id={id}
      className={`rounded-2xl border p-4 sm:p-5 ${
        bloqueado ? 'border-border bg-muted/40 opacity-60'
          : hecho ? 'border-emerald-300 bg-emerald-50/40' : 'border-border bg-white'
      }`}
      data-testid={`paso-${n}`}
      aria-disabled={bloqueado || undefined}
    >
      <div className="flex items-center gap-3 mb-3">
        <span
          className={`shrink-0 w-9 h-9 rounded-full grid place-items-center font-bold ${
            bloqueado ? 'bg-muted-foreground/40 text-white'
              : hecho ? 'bg-emerald-600 text-white' : 'bg-[#2E6E5E] text-white'
          }`}
        >
          {bloqueado ? <Lock className="w-4 h-4" /> : hecho ? <Check className="w-5 h-5" /> : n}
        </span>
        <h2 className="font-semibold text-[#13231E] flex items-center gap-2 text-[15px]">
          <Icono className="w-4 h-4 text-[#2E6E5E]" /> {titulo}
        </h2>
        {hecho && !bloqueado && (
          <span className="ml-auto text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">
            Hecho
          </span>
        )}
      </div>
      {bloqueado ? (
        <p className="text-sm text-muted-foreground" data-testid={`paso-${n}-bloqueado`}>
          Termina el paso anterior y se abre.
        </p>
      ) : children}
    </section>
  );
}

/** Botón que abre un PDF en una pestaña nueva (enlace real: el móvil lo respeta). */
function BotonPdf({ href, children, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-lg bg-[#2E6E5E] px-3 py-2 text-sm font-medium text-white hover:bg-[#1E4A3F]"
      data-testid={testid}
    >
      {children}
    </a>
  );
}

/**
 * Texto con formato de WhatsApp (*negrita*, _cursiva_) pintado como HTML.
 * El texto de pago lo dicta la clínica en ese formato y vive UNA sola vez en el
 * backend; aquí no se enseñan los asteriscos (2026-09-03). Las líneas de la
 * pauta postoperatoria usan la misma regla (2026-09-08).
 */
const TROZOS_FORMATO = /(\*[^*\n]+\*|_[^_\n]+_)/g;

function lineaConFormato(linea) {
  return linea.split(TROZOS_FORMATO).filter(Boolean).map((t, i) => {
    if (t.length > 2 && t.startsWith('*') && t.endsWith('*')) return <strong key={i}>{t.slice(1, -1)}</strong>;
    if (t.length > 2 && t.startsWith('_') && t.endsWith('_')) return <em key={i}>{t.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{t}</React.Fragment>;
  });
}

/**
 * La aclaración en cursiva que cierra una línea de la pauta (la de las
 * tiritas vive DENTRO de la línea de cada producto de la herida desde
 * el 2026-09-15), separada del producto que la lleva delante.
 * La etiqueta «solo con herida quirúrgica» califica al producto: detrás de
 * la aclaración, el paciente la lee cerrando una frase que ya no habla de
 * ese producto.
 */
const ACLARACION_FINAL = /\s*(_[^_\n]+_)\s*$/;

function productoYAclaracion(texto) {
  const linea = String(texto || '');
  const cierre = linea.match(ACLARACION_FINAL);
  return cierre ? [linea.slice(0, cierre.index), cierre[1]] : [linea, ''];
}

function TextoWhatsApp({ texto, testid }) {
  return (
    <div className="space-y-2 text-sm text-[#13231E]" data-testid={testid}>
      {String(texto || '').split('\n\n').map((parrafo, i) => (
        <p key={i}>
          {parrafo.split('\n').map((l, j) => (
            <React.Fragment key={j}>{j > 0 && <br />}{lineaConFormato(l)}</React.Fragment>
          ))}
        </p>
      ))}
    </div>
  );
}

/** Orden de los seis pasos de una cirugía. Un retoque llega con los cinco
 * suyos en `pasos_orden` (2026-09-08, punto 8): 1 instrucciones,
 * 2 pruebas como mensaje (sin subida), 3 anestesia, 4 cuidados como mensaje
 * (sin pauta), 5 consentimientos; sin paso de pago. */
const ORDEN_CIRUGIA = ['instrucciones', 'preoperatorio', 'biometria', 'consentimientos', 'postoperatorio', 'pago'];

/** Lo que se le dice si la cirugía no va a ser y el servidor no manda su
 * texto (versiones anteriores al 22/9/2026 solo daban {estado, nombre}). */
const MENSAJE_ANULADA = 'Tu cirugía está anulada. Si se reprograma te mandaremos una guía nueva.';

/** Las frases del mensaje de cierre: la primera (qué pasa) va en grande y el
 * resto (qué viene después) en pequeño. Sin expresiones con lookbehind: los
 * iPhone viejos no las entienden y se llevarían la página entera. */
function frasesDe(mensaje) {
  const frases = String(mensaje || '').split('. ').filter(Boolean);
  return frases.map((f, i) => (i < frases.length - 1 ? `${f}.` : f));
}

export default function MisPasosCirugiaPage() {
  const { token } = useParams();
  const [datos, setDatos] = useState(null);
  // cargando | listo | anulada (la cirugía está cancelada: el enlace es el
  // suyo, pero no hay guía) | invalido (el enlace de verdad no vale) | error
  // (no se ha podido consultar). Los tres últimos NO son lo mismo y no se
  // dicen igual: ver `cargar` (21/9/2026; anulada desde el 22/9/2026, G11).
  // Y `incompleto`: `/mis-pasos/` sin token (el enlace cortado al copiarlo)
  // iba al login del PERSONAL; ahora se dice que está incompleto (23/9/2026).
  const [estado, setEstado] = useState(token ? 'cargando' : 'incompleto');
  // El fallo al abrir la guía, para decir si fue la conexión o el servidor
  // y, en un 429, cuánto esperar.
  const [falloCarga, setFalloCarga] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [peso, setPeso] = useState('');
  const [altura, setAltura] = useState('');
  // ¿Ha escrito peso o altura que todavía no consta como guardado? Mientras
  // sí, lo del servidor no pisa los campos (23/9/2026): si el POST perdía la
  // contestación y el servidor decía que no había llegado, `aplicar()` los
  // dejaba vacíos y la paciente tenía que volver a escribirlos. Y lo mismo
  // con cualquier otra acción que recarga la guía (subir la analítica) a
  // mitad de escribirlos. Es una ref y no un estado: se lee dentro de
  // `aplicar`, que vive en callbacks ya creados.
  const bioSinGuardarRef = useRef(false);
  const [guardandoBio, setGuardandoBio] = useState(false);
  const [abriendoFirma, setAbriendoFirma] = useState(false);
  const camaraRef = useRef(null);
  const consentCamaraRef = useRef(null);
  const consentGaleriaRef = useRef(null);
  const [subiendoConsent, setSubiendoConsent] = useState(false);
  const galeriaRef = useRef(null);

  const base = `/api/public/cirugia-pasos/${encodeURIComponent(token)}`;
  const baseAbs = `${BACKEND_URL}${base}`;
  // El teléfono de la clínica para los avisos (23/9/2026): el de su guía si
  // ya se cargó; si no, el de Configuración o el último que este dispositivo
  // leyó bien. «Llámanos» sin número no le sirve a nadie, pero uno inventado
  // (el de otra clínica) menos: sin ninguno, «llama a la clínica».
  const telefonoClinica = useTelefonoClinica(datos?.clinica?.telefono, estado !== 'cargando');
  const llamanosClinica = aQuienLlamar(telefonoClinica);

  /** Pinta lo que contesta el servidor: la guía o la cirugía anulada. */
  const aplicar = (data) => {
    setDatos(data);
    // Cirugía ANULADA (G11, 22/9/2026): el servidor contesta solo
    // {estado: "cancelada", nombre}, sin fecha, sin pasos y sin datos de
    // pago. Se dice tal cual, también si la anulan mientras tiene la
    // guía abierta (una recarga tras una acción llega aquí igual).
    if (data?.estado === 'cancelada') {
      setEstado('anulada');
      return;
    }
    setEstado('listo');
    // Lo que está escribiendo y aún no consta como guardado no se pisa.
    if (bioSinGuardarRef.current) return;
    setPeso(data?.pasos?.biometria?.peso ? String(data.pasos.biometria.peso) : '');
    setAltura(data?.pasos?.biometria?.altura ? String(data.pasos.biometria.altura) : '');
  };

  /**
   * Trae los pasos del paciente.
   *
   * UNA AVERÍA NO ES UN ENLACE CADUCADO (21/9/2026). Antes, cualquier fallo
   * del GET —un 500, el 502 del proxy, un corte de red— acababa en «Enlace no
   * válido. Puede que haya caducado. Llámanos y te mandamos uno nuevo»: el
   * paciente que iba a firmar sus consentimientos o a subir la analítica leía
   * que su enlace ya no vale y dejaba de intentarlo, o llamaba a pedir uno
   * que no hacía falta. Si el que se cae es el backend, eso lo leen a la vez
   * todos los pacientes con cirugía esa semana. Solo un 404/410 significa de
   * verdad que el enlace no existe o ha caducado.
   *
   * `refresco` = recarga después de una acción suya (subir, quitar, «lo he
   * leído», peso y altura). Esas NUNCA tiran la pantalla: lo que acaba de
   * hacer ya está guardado en el servidor, y borrarle la guía justo después
   * le hace creer que lo ha perdido. Se deja lo que ya había y se avisa.
   */
  const cargar = useCallback(({ refresco = false } = {}) => (
    !token ? Promise.resolve() : api.get(base)
      .then((r) => aplicar(r.data))
      .catch((err) => {
        if (refresco) {
          toast.error('No hemos podido actualizar la pantalla. Lo que acabas de hacer sí se ha guardado.');
          return;
        }
        setFalloCarga(err);
        const codigo = err?.response?.status;
        // Un 429 es el freno de la guía (M27, 22/9/2026: 240 peticiones por
        // hora e IP, y la wifi de la clínica o una IP de CGNAT la comparten
        // muchos). Ni avería nuestra ni enlace caducado: esperar un rato.
        if (codigo === 429) {
          setEstado('ocupado');
          return;
        }
        setEstado(codigo === 404 || codigo === 410 ? 'invalido' : 'error');
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [token]);

  useEffect(() => { cargar(); }, [cargar]);

  const reintentar = () => { setEstado('cargando'); cargar(); };

  /**
   * La contestación de una acción se perdió (sin respuesta o 5xx del proxy):
   * pudo guardarse igual. Antes de decir «no se pudo» se le pregunta al
   * servidor (23/9/2026). Con mala cobertura la analítica puede llegar y
   * perderse la contestación: el paciente leía «No se pudo subir», la volvía
   * a subir o creía que no la tenemos. Si consta, se dice que está; si no
   * consta, que no ha llegado;
   * si ni eso se puede preguntar, que no se sabe — nunca un «no» inventado.
   */
  const comprobarTrasFallo = async ({ hecho, ok, noLlego, alLlegar }) => {
    let data;
    try {
      ({ data } = await api.get(base));
    } catch {
      toast.error('No hemos podido comprobar si se ha guardado. Comprueba tu conexión y recarga '
        + `la página para verlo. Si sigue sin funcionar, ${llamanosClinica}.`);
      return;
    }
    // Si consta, lo del servidor ya es lo que escribió: se puede enseñar.
    if (data?.estado !== 'cancelada' && hecho(data) && alLlegar) alLlegar();
    aplicar(data);
    if (data?.estado === 'cancelada') return; // la pantalla ya lo dice
    if (hecho(data)) {
      if (ok) toast.success(ok);
      return;
    }
    toast.error(noLlego);
  };

  /**
   * Una acción que falla. Un 410 es «tu cirugía está anulada» (G11,
   * 22/9/2026): la anularon con la guía abierta. No vale un toast que se
   * pierde con la guía entera detrás como si siguiera en pie: se vuelve a
   * pedir el estado y la pantalla lo dice. Un 429 (el freno: 20 subidas por
   * hora) dice cuándo volver a intentarlo. Sin respuesta o 5xx, se comprueba
   * (`comprobar`). Cualquier otro fallo, el mensaje del servidor o el de
   * siempre.
   */
  const errorDeAccion = async (err, porDefecto, comprobar) => {
    const codigo = err?.response?.status;
    if (codigo === 410) {
      cargar();
      return;
    }
    if (codigo === 429) {
      toast.error(`Hay mucho movimiento desde tu conexión. Vuelve a intentarlo ${cuandoReintentar(err)}.`);
      return;
    }
    if (comprobar && respuestaPerdida(err)) {
      await comprobarTrasFallo(comprobar);
      return;
    }
    toast.error(mensajeError(err, porDefecto));
  };

  /** Los ids de lo que ya había subido en ese paso, para saber tras una
   *  contestación perdida si lo nuevo llegó. */
  const idsDe = (d, paso) => (d?.pasos?.[paso]?.archivos || []).map((a) => a.id);
  const llegoAlgoNuevo = (paso, antes) => (d) => idsDe(d, paso).some((id) => !antes.includes(id));

  // El WhatsApp de preparación enlaza con #consentimientos / #pruebas / #video
  // / #pago: al cargar, llevamos al paciente directo a esa sección.
  useEffect(() => {
    if (estado !== 'listo') return;
    const ancla = window.location.hash.slice(1);
    if (!ancla) return;
    const t = setTimeout(() => {
      document.getElementById(ancla)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [estado]);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    const antes = idsDe(datos, 'preoperatorio');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`${base}/pruebas`, fd, { timeout: 180000 });
      toast.success('Prueba subida. ¡Gracias!');
      cargar({ refresco: true });
    } catch (err) {
      await errorDeAccion(err, 'No se pudo subir. Inténtalo otra vez.', {
        hecho: llegoAlgoNuevo('preoperatorio', antes),
        ok: 'Prueba subida. ¡Gracias!',
        noLlego: 'La prueba no ha llegado. Comprueba tu conexión e inténtalo otra vez.',
      });
    } finally {
      setSubiendo(false);
      if (camaraRef.current) camaraRef.current.value = '';
      if (galeriaRef.current) galeriaRef.current.value = '';
    }
  };

  const subirConsentimiento = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendoConsent(true);
    const antes = idsDe(datos, 'consentimientos');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`${base}/consentimientos`, fd, { timeout: 180000 });
      toast.success('Consentimiento subido. ¡Gracias!');
      cargar({ refresco: true });
    } catch (err) {
      await errorDeAccion(err, 'No se pudo subir. Inténtalo otra vez.', {
        hecho: llegoAlgoNuevo('consentimientos', antes),
        ok: 'Consentimiento subido. ¡Gracias!',
        noLlego: 'El consentimiento no ha llegado. Comprueba tu conexión e inténtalo otra vez.',
      });
    } finally {
      setSubiendoConsent(false);
      if (consentCamaraRef.current) consentCamaraRef.current.value = '';
      if (consentGaleriaRef.current) consentGaleriaRef.current.value = '';
    }
  };

  const borrarPrueba = async (id) => {
    if (!window.confirm('¿Quitar este archivo?')) return;
    try {
      await api.delete(`${base}/pruebas/${id}`);
      cargar({ refresco: true });
    } catch (err) {
      await errorDeAccion(err, 'No se pudo quitar', {
        hecho: (d) => ![...idsDe(d, 'preoperatorio'), ...idsDe(d, 'consentimientos')].includes(id),
        ok: null,
        noLlego: 'No se ha podido quitar. Comprueba tu conexión e inténtalo otra vez.',
      });
    }
  };

  const marcarLeido = async (paso) => {
    try {
      await api.post(`${base}/leido`, { paso });
      cargar({ refresco: true });
    } catch (err) {
      await errorDeAccion(err, 'No se pudo guardar', {
        hecho: (d) => !!(d?.pasos?.[paso]?.hecho || d?.pasos?.[paso]?.visto),
        ok: null,
        noLlego: 'No se ha guardado. Comprueba tu conexión e inténtalo otra vez.',
      });
    }
  };

  const guardarBio = async () => {
    const p = parseFloat(String(peso).replace(',', '.'));
    const a = parseFloat(String(altura).replace(',', '.'));
    if (!p || !a) return toast.error('Pon tu peso y tu altura');
    setGuardandoBio(true);
    try {
      await api.post(`${base}/biometria`, { peso: p, altura: a });
      // Ya consta: a partir de aquí manda lo del servidor.
      bioSinGuardarRef.current = false;
      toast.success('Guardado. Gracias');
      cargar({ refresco: true });
    } catch (err) {
      // Si no consta, lo que escribió se queda en los campos (ver
      // `bioSinGuardarRef`) para volver a pulsar Guardar sin reescribirlo.
      await errorDeAccion(err, 'No se pudo guardar', {
        hecho: (d) => !!d?.pasos?.biometria?.hecho
          && Number(d.pasos.biometria.peso) === p && Number(d.pasos.biometria.altura) === a,
        ok: 'Guardado. Gracias',
        noLlego: 'No se han guardado tu peso y tu altura. Comprueba tu conexión e inténtalo otra vez.',
        alLlegar: () => { bioSinGuardarRef.current = false; },
      });
    } finally {
      setGuardandoBio(false);
    }
  };

  // Leer y firmar con el dedo: el backend prepara el mismo enlace de firma
  // que usa recepción y nos vamos allí; al terminar, «Volver a mis pasos».
  const abrirFirma = async () => {
    setAbriendoFirma(true);
    try {
      const { data } = await api.post(`${base}/consentimientos/firmar`);
      if (data?.url) {
        window.location.assign(data.url);
        return;
      }
      // Ninguno se ha podido preparar (su plantilla ya no está en el
      // catálogo). No se le puede decir «no queda ninguno por firmar»:
      // quedan, y repetir no va a arreglarlo (21/9/2026). La clínica ya
      // tiene el aviso en la campana.
      if (data?.no_preparables) {
        toast.error('No hemos podido preparar tus consentimientos. Ya hemos avisado a la clínica: '
          + 'te llamarán o los firmarás allí. No hace falta que vuelvas a intentarlo.');
        cargar({ refresco: true });
        return;
      }
      toast.info('No queda ningún consentimiento por firmar.');
      cargar({ refresco: true });
    } catch (err) {
      await errorDeAccion(err, 'No se pudo abrir la firma. Inténtalo otra vez.');
    } finally {
      setAbriendoFirma(false);
    }
  };

  const copiarIban = async () => {
    try {
      await navigator.clipboard.writeText(datos.pasos.pago.iban);
      toast.success('IBAN copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  // Cirugía que no va a ser (G11, 22/9/2026): se dice, y nada más. Ni fecha,
  // ni lugar, ni pasos, ni datos de pago: no hay nada que preparar ni que
  // ingresar. El TEXTO lo manda el servidor (`mensaje`), porque el motivo
  // no siempre es una anulación: si la paciente no acudió a quirófano
  // («no acude», segunda pasada del 22/9) se le dice que la cirugía no
  // llegó a realizarse, no que está anulada; y es el mismo texto que llega
  // en el 410 de una acción, así que la pantalla y el error dicen lo mismo.
  // Si la reprograman con la misma cita, este mismo enlace vuelve a abrir
  // la guía; si es con otra, le llegará una nueva.
  if (estado === 'anulada') {
    const [primera, ...resto] = frasesDe(datos?.mensaje || MENSAJE_ANULADA);
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div data-testid="pasos-anulada">
          <h1 className="text-lg font-semibold text-[#13231E] mb-2">
            {datos?.nombre ? `Hola, ${datos.nombre}` : 'Hola'}
          </h1>
          <p className="text-base text-[#13231E] mb-1" data-testid="pasos-anulada-motivo">{primera}</p>
          {resto.length > 0 && (
            <p className="text-sm text-muted-foreground">{resto.join(' ')}</p>
          )}
          <p className="text-xs text-muted-foreground mt-4">
            Si algo no te cuadra, {llamanosClinica} y lo vemos.
          </p>
        </div>
      </div>
    );
  }
  if (estado === 'invalido') {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div data-testid="pasos-invalido">
          <h1 className="text-lg font-semibold text-[#13231E] mb-2">Enlace no válido</h1>
          <p className="text-sm text-muted-foreground">
            Puede que haya caducado. {conMayuscula(llamanosClinica)} y te mandamos uno nuevo.
          </p>
        </div>
      </div>
    );
  }
  // Sin token en la URL (23/9/2026): antes esto era el login del personal.
  if (estado === 'incompleto') {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div data-testid="pasos-incompleto">
          <h1 className="text-lg font-semibold text-[#13231E] mb-2">Enlace incompleto</h1>
          <p className="text-sm text-muted-foreground">
            {/* En una sola expresión: partido entre el texto y el {…}, el
                JSX se comía el espacio tras la coma (23/9/2026). */}
            {`Ábrelo desde el mensaje completo que te enviamos. Si no lo encuentras, ${llamanosClinica} y te lo mandamos de nuevo.`}
          </p>
        </div>
      </div>
    );
  }
  // La avería, dicha como lo que es: el enlace sigue siendo el suyo y no hay
  // que pedir otro (21/9/2026). Con un botón para volver a intentarlo, que es
  // lo único que hace falta cuando lo que se ha caído es el servidor.
  // `ocupado` (el 429 del freno, 22/9/2026) usa la misma pantalla con su
  // propio texto, como ConfirmarCitaPage: esperar un rato y reintentar.
  if (estado === 'error' || estado === 'ocupado') {
    const ocupado = estado === 'ocupado';
    return (
      <div className="min-h-screen grid place-items-center p-6 text-center">
        <div data-testid={ocupado ? 'pasos-ocupado' : 'pasos-error'}>
          <h1 className="text-lg font-semibold text-[#13231E] mb-2">
            {ocupado ? 'Inténtalo dentro de un rato, por favor' : 'No hemos podido cargar tus pasos ahora mismo'}
          </h1>
          {/* Qué pasa de verdad (23/9/2026): el 429 dice cuánto esperar; sin
              respuesta no sabemos si el corte es suyo o nuestro, así que se le
              pide mirar su conexión; un 5xx es nuestro. */}
          <p className="text-sm text-muted-foreground mb-4">
            {ocupado
              ? `Hay mucho movimiento en este momento. Podrás volver a entrar ${cuandoReintentar(falloCarga)}. Tu enlace sigue siendo válido.`
              : !falloCarga?.response
                ? 'Comprueba tu conexión y vuelve a intentarlo. Tu enlace sigue siendo válido.'
                : 'Vuelve a intentarlo en un momento. Tu enlace sigue siendo válido.'}
          </p>
          <Button onClick={reintentar} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="pasos-reintentar">
            Volver a intentarlo
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Si sigue sin cargar, {llamanosClinica}.
          </p>
        </div>
      </div>
    );
  }

  const p = datos.pasos;
  const c = p.consentimientos;
  const po = p.postoperatorio;
  // Variante y orden: los dice el backend (seis pasos en una cirugía, cinco
  // en un retoque), para numerar y contar lo mismo que la clínica.
  const esRetoque = !!datos.es_retoque;
  const orden = datos.pasos_orden?.length ? datos.pasos_orden : ORDEN_CIRUGIA;

  // Los pasos van EN ORDEN. Un paso "pasa" cuando está hecho o cuando no hay
  // nada que hacer todavía (la clínica aún no lo ha preparado): si no, el
  // paciente se quedaría atascado esperando un documento que no depende de él.
  const pasa = {
    instrucciones: p.instrucciones.hecho || !p.instrucciones.disponible,
    // El paso de pruebas depende del LABORATORIO, no del paciente: puede
    // tardar días. No puede tener secuestrado el peso y la altura, que se dan
    // en 5 segundos. Con decir "todavía no las tengo" se sigue; el paso queda
    // pendiente igualmente. (En un retoque es un mensaje: pasa al leerlo.)
    preoperatorio: p.preoperatorio.hecho || p.preoperatorio.visto || !p.preoperatorio.disponible,
    biometria: p.biometria.hecho,
    // Los consentimientos se firman en otra pantalla (o en la clínica): con
    // abrir la firma o decir «los firmaré allí» se sigue; el paso queda
    // pendiente hasta que estén todos firmados.
    consentimientos: c.hecho || c.visto || !c.disponible,
    postoperatorio: po.hecho || !po.disponible,
    pago: !!p.pago?.hecho,
  };
  // Un paso se abre cuando todos los anteriores (en el orden de la variante) pasan.
  const abierto = {};
  const numero = {};
  let anterioresOk = true;
  orden.forEach((clave, i) => {
    abierto[clave] = anterioresOk;
    numero[clave] = i + 1;
    anterioresOk = anterioresOk && !!pasa[clave];
  });
  const hechos = orden.filter((clave) => p[clave]?.hecho).length;
  // Los productos de la herida se destacan en rojo cuando NO es seguro que
  // haya herida (una intervención sin incisión, o cirugía sin identificar).
  const destacarHerida = Boolean(po.aviso_herida);

  // Cada paso como función: solo se construye el que va en la variante
  // (un retoque no tiene paso de pago y `p.pago` no existe).
  const bloques = {
    // 1 — Instrucciones preoperatorias
    instrucciones: () => (
        <Paso n={numero.instrucciones} id="instrucciones" titulo="Instrucciones preoperatorias" hecho={p.instrucciones.hecho} icono={ClipboardList}>
          {p.instrucciones.disponible ? (
            <div className="flex flex-wrap gap-2">
              <BotonPdf href={`${baseAbs}/pdf/instrucciones`} testid="pdf-instrucciones">
                <FileText className="w-4 h-4" /> Ver instrucciones
              </BotonPdf>
              <a
                href={`${baseAbs}/pdf/instrucciones?descargar=1`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#2E6E5E] px-3 py-2 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                data-testid="descargar-instrucciones"
              >
                <Download className="w-4 h-4" /> Descargar
              </a>
              {!p.instrucciones.hecho && (
                <Button variant="outline" onClick={() => marcarLeido('instrucciones')} data-testid="leido-instrucciones">
                  <Check className="w-4 h-4 mr-1.5" /> Las he leído
                </Button>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Todavía no están preparadas. Las tendrás aquí en cuanto la clínica las genere.
            </p>
          )}
        </Paso>
    ),
    // 2 — Solicitud de pruebas preoperatorias + subir las pruebas (van a
    // la pestaña «Pruebas preoperatorias» de la ficha). Sin «volante»
    // (2026-09-03).
    preoperatorio: () => (
        <Paso n={numero.preoperatorio} id="pruebas" titulo="Aportación de pruebas preoperatorias" hecho={p.preoperatorio.hecho} bloqueado={!abierto.preoperatorio} icono={Stethoscope}>
          {esRetoque ? (
            <>
              {/* Retoque (2026-09-08): ya tenemos su documentación de
                  la intervención anterior; el mensaje dictado, sin subida. */}
              <p className="text-sm text-[#13231E]" data-testid="preop-retoque-mensaje">
                {p.preoperatorio.mensaje}
              </p>
              {!p.preoperatorio.hecho && (
                <Button variant="outline" className="mt-3" onClick={() => marcarLeido('preoperatorio')} data-testid="leido-preoperatorio">
                  <Check className="w-4 h-4 mr-1.5" /> Entendido
                </Button>
              )}
            </>
          ) : (
            <>

          {p.preoperatorio.disponible && (
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                <BotonPdf href={`${baseAbs}/pdf/preoperatorio`} testid="pdf-preoperatorio">
                  <FileText className="w-4 h-4" /> Ver la solicitud
                </BotonPdf>
                <a
                  href={`${baseAbs}/pdf/preoperatorio?descargar=1`}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-[#2E6E5E] px-3 py-2 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                  data-testid="descargar-preoperatorio"
                >
                  <Download className="w-4 h-4" /> Descargar
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Llévalo a que te hagan la analítica y el electrocardiograma.
              </p>
            </div>
          )}

          {/* Cámara y galería por separado: en el móvil son dos cosas distintas. */}
          <input
            ref={camaraRef} type="file" accept="image/*" capture="environment"
            className="hidden" onChange={subir}
          />
          <input
            ref={galeriaRef} type="file"
            accept="application/pdf,image/*,.heic,.heif"
            className="hidden" onChange={subir}
          />
          {/* Solo la analítica se sube (2026-08-05): una foto de móvil de la
              tira del electro no se puede leer —papel largo, cuadrícula fina y
              reflejos—, así que ese se trae en papel. */}
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 mb-3">
            <p className="text-sm text-amber-900">
              <strong>Sube aquí solo la analítica.</strong>
            </p>
            <p className="text-xs text-amber-900/90 mt-0.5">
              El electrocardiograma <strong>tráelo en papel</strong> el día de la
              cirugía: en una foto no se puede leer bien.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline" disabled={subiendo}
              onClick={() => camaraRef.current?.click()}
              data-testid="subir-camara"
            >
              {subiendo ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
              Foto de la analítica
            </Button>
            <Button
              variant="outline" disabled={subiendo}
              onClick={() => galeriaRef.current?.click()}
              data-testid="subir-galeria"
            >
              <ImageIcon className="w-4 h-4 mr-1.5" /> Subir PDF o imagen
            </Button>
            {!p.preoperatorio.hecho && !p.preoperatorio.visto && (
              <Button
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => marcarLeido('preoperatorio')}
                data-testid="preop-todavia-no"
              >
                Todavía no la tengo
              </Button>
            )}
          </div>
          {!p.preoperatorio.hecho && p.preoperatorio.visto && (
            <p className="text-xs text-amber-800 mt-2" data-testid="preop-pendiente">
              Pendiente: sube la analítica en cuanto te la den.
            </p>
          )}

          {p.preoperatorio.archivos?.length > 0 && (
            <ul className="mt-3 space-y-1.5" data-testid="pruebas-subidas">
              {p.preoperatorio.archivos.map((a) => (
                <li key={a.id} className="flex items-center gap-2 text-sm rounded-lg border border-border bg-white px-2.5 py-1.5">
                  <FileText className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                  <a
                    href={`${baseAbs}/prueba/${a.id}`} target="_blank" rel="noopener noreferrer"
                    className="flex-1 min-w-0 truncate hover:underline"
                  >
                    {a.name}
                  </a>
                  <button
                    type="button" onClick={() => borrarPrueba(a.id)}
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    title="Quitar"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
            </>
          )}
        </Paso>
    ),
    // 3 — Peso y altura para la anestesia
    biometria: () => (
        <Paso n={numero.biometria} id="anestesia" titulo="Datos para la anestesia" hecho={p.biometria.hecho} bloqueado={!abierto.biometria} icono={Scale}>
          <p className="text-sm text-muted-foreground mb-3">
            Tu peso y tu altura: los necesita el anestesista para calcular tu medicación.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Peso (kg)</Label>
              <Input
                type="number" inputMode="decimal" step="0.1" min="1"
                value={peso} onChange={(e) => { bioSinGuardarRef.current = true; setPeso(e.target.value); }}
                placeholder="70" className="mt-1" data-testid="bio-peso"
              />
            </div>
            <div>
              <Label className="text-xs">Altura (cm)</Label>
              <Input
                type="number" inputMode="numeric" min="1"
                value={altura} onChange={(e) => { bioSinGuardarRef.current = true; setAltura(e.target.value); }}
                placeholder="170" className="mt-1" data-testid="bio-altura"
              />
            </div>
          </div>
          <Button
            className="mt-3 w-full bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            onClick={guardarBio} disabled={guardandoBio}
            data-testid="bio-guardar"
          >
            {guardandoBio ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
          </Button>
        </Paso>
    ),
    // 4 — Consentimientos: leerlos y firmarlos con el dedo (2026-09-03).
    // La firma queda en la ficha, en Consentimientos; el de anestesia
    // lo firma además el anestesista desde Quirófano. Si se firmaron
    // en papel, aquí se sube la foto (2026-08-25).
    consentimientos: () => (
        <Paso n={numero.consentimientos} id="consentimientos" titulo="Lectura de consentimientos informados" hecho={c.hecho} bloqueado={!abierto.consentimientos} icono={FilePen}>
          {c.total > 0 ? (
            <>
              <div className="text-sm text-[#13231E] mb-1" data-testid="consentimientos-resumen">
                {c.firmados} de {c.total} firmados
              </div>
              <ul className="space-y-1" data-testid="consentimientos-lista">
                {c.detalle.map((ci, i) => (
                  <li key={i} className="text-sm flex items-center gap-2">
                    <span className={ci.firmado ? 'text-green-700' : 'text-amber-700'}>
                      {ci.firmado ? '✓' : '○'}
                    </span>
                    <span className={ci.firmado ? '' : 'text-muted-foreground'}>{ci.nombre}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="consentimientos-vacio">
              Todavía no hay consentimientos preparados. Los tendrás aquí en cuanto la clínica los genere.
            </p>
          )}
          {!c.hecho && (
            <div className="flex flex-wrap gap-2 mt-3">
              <Button
                className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
                onClick={abrirFirma} disabled={abriendoFirma}
                data-testid="consent-firmar"
              >
                {abriendoFirma ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FilePen className="w-4 h-4 mr-1.5" />}
                Leer y firmar los consentimientos
              </Button>
              {!c.visto && (
                <Button
                  variant="ghost" className="text-muted-foreground"
                  onClick={() => marcarLeido('consentimientos')}
                  data-testid="consent-en-clinica"
                >
                  Los firmaré en la clínica
                </Button>
              )}
            </div>
          )}
          {!c.hecho && c.visto && (
            <p className="text-xs text-amber-800 mt-2" data-testid="consent-pendiente">
              Pendiente: los que falten los firmarás en la clínica antes de la intervención.
            </p>
          )}
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-sm text-[#13231E] mb-2">
              ¿Los has firmado en papel? Haz una foto de cada hoja firmada y súbela aquí.
            </p>
            <input
              ref={consentCamaraRef} type="file" accept="image/*" capture="environment"
              className="hidden" onChange={subirConsentimiento}
            />
            <input
              ref={consentGaleriaRef} type="file"
              accept="application/pdf,image/*,.heic,.heif"
              className="hidden" onChange={subirConsentimiento}
            />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline" disabled={subiendoConsent}
                onClick={() => consentCamaraRef.current?.click()}
                data-testid="subir-consent-camara"
              >
                {subiendoConsent ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                Foto del consentimiento
              </Button>
              <Button
                variant="outline" disabled={subiendoConsent}
                onClick={() => consentGaleriaRef.current?.click()}
                data-testid="subir-consent-galeria"
              >
                <ImageIcon className="w-4 h-4 mr-1.5" /> Subir PDF o imagen
              </Button>
            </div>
            {c.archivos?.length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="consentimientos-subidos">
                {c.archivos.map((a) => (
                  <li key={a.id} className="flex items-center gap-2 text-sm rounded-lg border border-border bg-white px-2.5 py-1.5">
                    <FileText className="w-4 h-4 text-[#2E6E5E] shrink-0" />
                    <a
                      href={`${baseAbs}/prueba/${a.id}`} target="_blank" rel="noopener noreferrer"
                      className="flex-1 min-w-0 truncate hover:underline"
                    >
                      {a.name}
                    </a>
                    <button
                      type="button" onClick={() => borrarPrueba(a.id)}
                      className="shrink-0 text-muted-foreground hover:text-destructive"
                      title="Quitar"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Paso>
    ),
    // 5 — Cuidados postoperatorios: la pauta, con los productos de la herida
    // en rojo cuando no es seguro que haya herida (2026-09-03). Sin
    // recetas: se entregan en mano el día de la cirugía.
    postoperatorio: () => (
        <Paso n={numero.postoperatorio} id="postoperatorio" titulo="Cuidados postoperatorios" hecho={po.hecho} bloqueado={!abierto.postoperatorio} icono={HeartPulse}>
          {esRetoque ? (
            <>
              {/* Retoque (2026-09-08): los cuidados se explican en la
                  clínica el día de la intervención; el mensaje dictado, sin pauta. */}
              <p className="text-sm text-[#13231E]" data-testid="postop-retoque-mensaje">
                {po.mensaje}
              </p>
              {!po.hecho && (
                <Button variant="outline" className="mt-3" onClick={() => marcarLeido('postoperatorio')} data-testid="leido-postoperatorio">
                  <Check className="w-4 h-4 mr-1.5" /> Entendido
                </Button>
              )}
            </>
          ) : (
            <>

          {/* El aviso va ANTES de los botones (revisión 2026-09-03): que se
              lea antes de abrir el PDF, que lleva los mismos productos marcados. */}
          {po.disponible && po.aviso_herida && (
            <div
              className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 flex items-start gap-2"
              data-testid="aviso-herida"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-[2px] text-red-600" />
              <span>{po.aviso_herida}</span>
            </div>
          )}

          {po.disponible ? (
            <div className="flex flex-wrap gap-2">
              <BotonPdf href={`${baseAbs}/pdf/postoperatorio`} testid="pdf-postoperatorio">
                <FileText className="w-4 h-4" /> Ver los cuidados
              </BotonPdf>
              <a
                href={`${baseAbs}/pdf/postoperatorio?descargar=1`}
                className="inline-flex items-center gap-1.5 rounded-lg border border-[#2E6E5E] px-3 py-2 text-sm font-medium text-[#13231E] hover:bg-[rgba(46,110,94,0.08)]"
                data-testid="descargar-postoperatorio"
              >
                <Download className="w-4 h-4" /> Descargar
              </a>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Los cuidados de después te los daremos con la cirugía.
            </p>
          )}

          {po.pauta?.length > 0 && (
            <div className="mt-3 space-y-3" data-testid="pauta-postop">
              {po.pauta.map((sec, i) => (
                <div key={i}>
                  {sec.titulo && (
                    <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                      {sec.titulo}
                    </div>
                  )}
                  {/* El cuerpo de la sección lleva el mismo formato de WhatsApp
                      que las líneas: nada de guiones bajos a la vista
                      (2026-09-09). */}
                  {sec.cuerpo && (
                    <p className="text-sm text-[#13231E] mb-1" data-testid="pauta-cuerpo">
                      {lineaConFormato(sec.cuerpo)}
                    </p>
                  )}
                  <ul className="space-y-1">
                    {sec.items.map((it, j) => {
                      const rojo = destacarHerida && it.solo_herida;
                      // En la línea marcada, la aclaración que la cierra se lee
                      // aparte: detrás de la etiqueta y sin el rojo del aviso,
                      // porque es la condición del producto y no una alarma más.
                      const [producto, aclaracion] = rojo ? productoYAclaracion(it.texto) : [it.texto, ''];
                      return (
                        <li
                          key={j}
                          className={`text-sm flex items-start gap-1.5 ${rojo ? 'text-red-700 font-medium' : 'text-[#13231E]'}`}
                          data-testid={rojo ? 'pauta-item-herida' : 'pauta-item'}
                        >
                          {rojo
                            ? <AlertTriangle className="w-4 h-4 shrink-0 mt-[2px] text-red-600" />
                            : <span className="shrink-0 mt-[2px] w-4 text-center text-[#2E6E5E]">•</span>}
                          <span>
                            {/* Las líneas de la pauta llevan formato de WhatsApp
                                (_cursiva_ para las aclaraciones, 2026-09-08). */}
                            {lineaConFormato(producto)}
                            {rojo && (
                              <span className="ml-1.5 inline-block rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-800 align-middle">
                                Solo con herida quirúrgica
                              </span>
                            )}
                            {aclaracion && (
                              <span className="ml-1.5 font-normal text-[#13231E]" data-testid="pauta-aclaracion">
                                {lineaConFormato(aclaracion)}
                              </span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {!po.hecho && (
            <div className="mt-3">
              <Button variant="outline" onClick={() => marcarLeido('postoperatorio')} data-testid="leido-postoperatorio">
                <Check className="w-4 h-4 mr-1.5" /> Los he leído
              </Button>
            </div>
          )}
            </>
          )}
        </Paso>
    ),
    // 6 — Información de pago (2026-09-03): texto dictado por la clínica, con
    // el concepto ya relleno con el nombre y los dos apellidos.
    pago: () => (
        <Paso n={numero.pago} id="pago" titulo="Información para realizar el pago" hecho={p.pago.hecho} bloqueado={!abierto.pago} icono={Landmark}>
          <TextoWhatsApp texto={p.pago.mensaje} testid="pago-texto" />
          <div className="flex flex-wrap gap-2 mt-3">
            {/* El texto pide el justificante «por este mismo WhatsApp», pero
                esto se lee en la web: el botón abre el chat de la clínica
                (revisión 2026-09-03). Sin teléfono configurado no se enseña.
                La cuenta sale de Configuración: sin IBAN el texto no trae datos
                bancarios, no hay justificante que mandar ni IBAN que copiar. */}
            {p.pago.iban && datos.clinica?.whatsapp && (
              <a
                href={`https://wa.me/${datos.clinica.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#2E6E5E] px-3 py-2 text-sm font-medium text-white hover:bg-[#1E4A3F]"
                data-testid="pago-whatsapp"
              >
                <MessageCircle className="w-4 h-4" /> Enviar justificante por WhatsApp
              </a>
            )}
            {p.pago.iban && (
              <Button variant="outline" onClick={copiarIban} data-testid="pago-copiar-iban">
                <Copy className="w-4 h-4 mr-1.5" /> Copiar IBAN
              </Button>
            )}
            {!p.pago.hecho && (
              <Button variant="outline" onClick={() => marcarLeido('pago')} data-testid="leido-pago">
                <Check className="w-4 h-4 mr-1.5" /> Entendido
              </Button>
            )}
          </div>
        </Paso>
    ),
  };

  // Vídeo del doctor con las pautas de después: enlazado directo desde
  // el WhatsApp de preparación (#video), por eso no se bloquea.
  const videoPostop = po.video_url ? (
          <div id="video" className="rounded-xl border border-border bg-white p-4" data-testid="pasos-video-postop">
            <div className="text-sm font-semibold text-[#13231E] mb-2">
              Vídeo de pautas postoperatorias
            </div>
            <a
              href={po.video_url}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#2E6E5E] px-3 py-2 text-sm font-medium text-white hover:bg-[#1E4A3F]"
              data-testid="ver-video-postop"
            >
              <PlayCircle className="w-4 h-4" /> Ver el vídeo del doctor
            </a>
          </div>
  ) : null;

  return (
    <div className="min-h-screen bg-[#F6F8F7] py-6 px-4">
      <div className="mx-auto w-full max-w-xl space-y-4">
        <header className="text-center">
          <h1 className="text-xl font-semibold text-[#13231E]">
            {datos.paciente?.nombre_completo || datos.paciente?.nombre || 'Hola'}
          </h1>
          {/* Fecha, hora y dónde: lo primero que el paciente necesita ver. */}
          {(datos.cirugia?.fecha || datos.cirugia?.lugar) ? (
            <div
              className="mt-2 inline-block rounded-xl border border-[#2E6E5E]/30 bg-white px-4 py-2.5 text-left"
              data-testid="cabecera-cirugia"
            >
              {datos.cirugia?.procedimiento && (
                <div className="text-[11px] font-semibold uppercase tracking-wide text-[#2E6E5E]">
                  {datos.cirugia.procedimiento}
                </div>
              )}
              {datos.cirugia?.fecha && (
                <div className="text-[15px] font-semibold text-[#13231E]">
                  {formatDate(datos.cirugia.fecha)}
                  {datos.cirugia?.hora ? <> · {datos.cirugia.hora} h</> : null}
                </div>
              )}
              {datos.cirugia?.lugar && (
                <div className="text-sm text-[#13231E] mt-0.5 flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#2E6E5E] shrink-0 mt-[3px]" />
                  <span>
                    <strong>{datos.cirugia.lugar}</strong>
                    {datos.cirugia?.direccion ? <><br />{datos.cirugia.direccion}</> : null}
                  </span>
                </div>
              )}
            </div>
          ) : null}
          <p className="text-sm text-muted-foreground mt-2">
            Estos son tus pasos antes {esRetoque ? 'del retoque' : 'de la cirugía'}.
          </p>
          <div className="mt-3 flex items-center justify-center gap-2" data-testid="pasos-progreso">
            {orden.map((clave, i) => (
              <span
                key={clave}
                className={`h-1.5 w-8 rounded-full ${i < hechos ? 'bg-emerald-600' : 'bg-border'}`}
              />
            ))}
            <span className="text-xs text-muted-foreground ml-1">{hechos} de {orden.length}</span>
          </div>
        </header>

        {orden.map((clave) => (
          <React.Fragment key={clave}>
            {bloques[clave]()}
            {clave === 'postoperatorio' && videoPostop}
          </React.Fragment>
        ))}

        <p className="text-center text-xs text-muted-foreground pt-2 pb-6">
          Si algo no te cuadra, llámanos y lo vemos.
        </p>
      </div>
    </div>
  );
}
